<template>
  <section id="depositPage">
    <!-- Invoice notice (card payments: Revolut + Stripe auto-issue invoices) -->
    <b-card no-body class="mb-2 invoice-info-card">
      <div class="invoice-info-row">
        <div class="ii-icon">
          <feather-icon icon="MailIcon" size="20" class="text-primary" />
        </div>
        <div class="ii-text">
          <strong>Invoice for card payments</strong>
          <small class="d-block text-muted">
            <template v-if="billingConfigured">
              Card payments (Revolut / Stripe) auto-issue an email invoice to:
              <b>{{ billingSummary }}</b>.
            </template>
            <template v-else>
              Card payments (Revolut / Stripe) auto-issue an email invoice using your
              registration name &amp; country. For a company invoice with your
              tax/VAT number, set up your billing details.
            </template>
          </small>
        </div>
        <b-button
          variant="flat-primary"
          size="sm"
          class="ii-action"
          :to="{ name: 'user-billing' }"
        >
          <feather-icon :icon="billingConfigured ? 'EditIcon' : 'PlusCircleIcon'" size="14" class="mr-25" />
          {{ billingConfigured ? 'Edit' : 'Set up' }}
        </b-button>
      </div>
    </b-card>

    <b-card class="deposit-card py-5">
      <DepositCallback />
      <b-card-text class="current-text-balance mb-2"> Balance: </b-card-text>
      <b-card-text class="current-balance mb-2"> {{ balance }} $ </b-card-text>
      <b-form-select class="mb-2" v-model="paymentMethod" :options="visibleOptions" />

      <!--
        Block Revolut Auto (V1) payments when billing is incomplete. Always-on
        — we want every V1 user to have proper billing data BEFORE the automatic-
        invoicing launch on June 1, 2026. Backend invoice generation is still
        disabled until then, so no incorrect invoice can be issued in the
        meantime; we're collecting the data ahead of time. Revolut Manual is
        NOT affected (admin-processed, doesn't auto-issue invoices).
      -->
      <b-alert
        v-if="isInvoicedMethod && !billingValidForRevolut"
        variant="warning"
        show
        class="mb-2"
      >
        <h5 class="alert-heading mb-1">
          <feather-icon icon="AlertTriangleIcon" size="16" class="mr-50" />
          Billing details required
        </h5>
        <p class="mb-1 small">
          <span>
            Card payments (Revolut / Stripe) auto-issue a tax invoice. We need at least your
          </span>
          <strong v-if="billing.account_type === 'business'">company name, tax/VAT ID, country and address</strong>
          <strong v-else>name, country and address</strong>
          before you can proceed.
        </p>
        <p class="mb-1 small text-muted">
          Don't want to add billing details? You can still deposit using Binance Pay, USDT, or a Gift Code — pick one from the dropdown above.
        </p>
        <b-button :to="{ name: 'user-billing' }" size="sm" variant="warning">
          <feather-icon icon="EditIcon" size="14" class="mr-25" />
          Complete billing details
        </b-button>
      </b-alert>

      <component
        v-else
        :style="{ position: 'relative' }"
        :is="paymentMethod"
        :between="between"
      />
    </b-card>
  </section>
</template>

<script>
import {
  BAlert,
  BButton,
  BRow,
  BCol,
  BCard,
  BCardText,
  BFormSelect,
} from "bootstrap-vue";
import MethodPaypal from "./MethodPaypal";
import MethodStripe from "./MethodStripe";
import MethodRevolut from "./MethodRevolut";
import MethodRevolutV1 from "./MethodRevolutV1";
import MethodStripeCheckout from "./MethodStripeCheckout.vue";
import MethodUsdT from "./MethodUsdT";
import MethodCode from "./MethodCode";
import MethodBinance from "./MethodBinance";
import { mapGetters, mapState } from "vuex";
import DepositCallback from "@/components/deposit/Callback.vue";
import MethodBinancePay from "./MethodBinancePay.vue";
import Deposit from "@services/deposit.service";

export default {
  data: () => ({
    paymentMethod: null,
    // enabledMethods: { method_key: bool } fetched from the backend admin kill
    // switch. null = not loaded yet (fail-open, show everything).
    enabledMethods: null,
    // `key` maps each option to the backend payment_method_settings.method_key
    // so a disabled method is hidden here (server also rejects it).
    options: [
      { text: "Select Payment Method :", value: null, disabled: true },
      { text: "Binance Pay Automatic Payment (instant)", value: "method-binance", key: "binance" },
      { text: "Card payment — instant (Visa / Mastercard via Revolut)", value: "method-revolut-v1", key: "revolut" },
      { text: "Card payment — Stripe (Visa / Mastercard) - instant", value: "method-stripe-checkout", key: "stripe" },
      { text: "Revolut (or Card) Manual Payment", value: "method-revolut", key: "revolut_manual" },
      { text: "USDT Manual Payment", value: "method-usdt", key: "usdt" },
      { text: "Use Voucher / Gift Code Payment", value: "method-code", key: "code" },
    ],
  }),
  computed: {
    ...mapGetters(["balance", "userGroup"]),
    ...mapState(["user"]),
    /**
     * Dropdown options with admin-disabled methods removed. Fail-open: while
     * enabledMethods is null (not yet loaded) or a key is absent from the map,
     * the option stays visible so a slow/failed fetch never empties the list.
     * The placeholder row (no key) is always kept.
     */
    visibleOptions() {
      if (!this.enabledMethods) return this.options;
      return this.options.filter(
        (o) => !o.key || this.enabledMethods[o.key] !== false
      );
    },
    between() {
      return this.userGroup == "RESELLER"
        ? {
            min: +process.env.VUE_APP_MIN_DEPOSIT_RESELLER,
            max: +process.env.VUE_APP_MAX_DEPOSIT_RESELLER,
          }
        : {
            min: +process.env.VUE_APP_MIN_DEPOSIT,
            max: +process.env.VUE_APP_MAX_DEPOSIT,
          };
    },
    billing() {
      const u = (this.user && this.user.user) || {};
      return {
        account_type: u.billing_default_type || "personal",
        full_name: u.billing_full_name || u.name || "",
        company_name: u.billing_company_name || "",
        tax_id: u.billing_tax_id || "",
        country: u.billing_country || u.country || "",
        address: u.billing_address || "",
        city: u.billing_city || "",
        county: u.billing_county || "",
      };
    },
    billingConfigured() {
      const b = this.billing;
      return !!(b.country && b.address);
    },
    /**
     * Stricter check used to gate Revolut payments — every issued invoice must
     * have at least the legally minimum buyer details (Codul Fiscal Art. 319).
     */
    billingValidForRevolut() {
      const b = this.billing;
      if (!b.country || !b.address) return false;
      // Romanian buyers go to ANAF e-Factura (SPV), which rejects invoices
      // without a real locality + county — require them before payment.
      if (b.country === "Romania" && (!b.city || !b.county)) return false;
      if (b.account_type === "business") {
        return !!(b.company_name && b.tax_id);
      }
      return !!b.full_name;
    },
    /**
     * Only the auto/instant Revolut flow (method-revolut-v1) triggers the
     * webhook → InvoiceIssuer chain that auto-issues a tax invoice. The
     * "Revolut Manual" option (method-revolut) is admin-processed and does
     * not currently auto-issue, so it stays unblocked.
     */
    isRevolutMethod() {
      return this.paymentMethod === "method-revolut-v1";
    },
    /**
     * Every payment method that auto-issues a tax invoice (via SmartBill)
     * requires complete billing details. Stripe joined Revolut in July 2026.
     * Revolut Manual / crypto / gift codes stay unblocked (no auto-invoice).
     */
    isInvoicedMethod() {
      return ["method-revolut-v1", "method-stripe-checkout"].includes(this.paymentMethod);
    },
    billingSummary() {
      const b = this.billing;
      if (!b.country) return "—";
      const who =
        b.account_type === "business"
          ? b.company_name || "Business"
          : b.full_name || "Individual";
      return `${who} · ${b.country}`;
    },
  },
  components: {
    BAlert,
    BButton,
    BCard,
    BCardText,
    BRow,
    BCol,
    BFormSelect,
    DepositCallback,
    "method-paypal": MethodPaypal,
    "method-stripe": MethodStripe,
    "method-stripe-checkout": MethodStripeCheckout,
    "method-revolut": MethodRevolut,
    "method-usdt": MethodUsdT,
    "method-code": MethodCode,
    "method-binance-pay": MethodBinancePay,
    "method-revolut-v1": MethodRevolutV1,
    "method-binance": MethodBinance,
  },
  methods: {
    /**
     * Returns true on/after the automatic-invoicing launch date (2026-06-01 RO time).
     * Used as a method (not computed) so it re-evaluates on every render — a computed
     * with no reactive deps would cache and stay stale if a tab is left open across
     * midnight.
     *
     * Role: switches the hard-block alert wording between future tense ("From June 1,
     * 2026, card payments will auto-issue...") and present tense ("Card payments
     * via Revolut auto-issue..."). The block ITSELF is always active for Revolut
     * Auto (V1) when billing is incomplete — independent of this date.
     */
    enforcementActive() {
      return new Date() >= new Date('2026-06-01T00:00:00+03:00');
    },
    /**
     * Fetch the admin kill-switch state and hide disabled methods. Fail-open:
     * on any error the dropdown keeps every method (enabledMethods stays null).
     * If the currently selected method just got disabled, clear the selection.
     */
    async loadEnabledMethods() {
      try {
        const map = await Deposit.enabledMethods();
        this.enabledMethods = map || null;
        const selected = this.options.find((o) => o.value === this.paymentMethod);
        if (selected && selected.key && this.enabledMethods && this.enabledMethods[selected.key] === false) {
          this.paymentMethod = null;
        }
      } catch (e) {
        this.enabledMethods = null;
      }
    },
  },
  mounted() {
    if (this.$route.query.voucher && this.$route.query.voucher != "") {
      this.paymentMethod = "method-code";
    }
    this.loadEnabledMethods();
  },
};
</script>

<style lang="scss" scoped>
.pay-method {
  min-height: 180px;
}
.current-text-balance {
  font-size: 20px;
  text-align: center;
}
.current-balance {
  font-size: 28px;
  font-weight: bold;
  text-align: center;
}
.deposit-card {
  justify-content: center;
  align-items: center;
  > .card-body {
    min-width: 35%;
  }
  .card-title {
    text-align: center;
  }
}
.invoice-info-card {
  .invoice-info-row {
    display: flex;
    align-items: center;
    padding: 0.85rem 1rem;
    gap: 0.85rem;
    .ii-icon { flex-shrink: 0; }
    .ii-text {
      flex: 1;
      min-width: 0;
      strong { font-size: 0.95rem; }
      small {
        margin-top: 0.15rem;
      }
    }
    .ii-action { flex-shrink: 0; }
  }
  .invoice-info-launch-note {
    display: flex;
    align-items: flex-start;
    gap: 0.4rem;
    padding: 0.65rem 1rem;
    border-top: 1px solid rgba(115, 103, 240, 0.18);
    background-color: rgba(115, 103, 240, 0.08);
    color: #7367f0;
    font-size: 0.8rem;
    line-height: 1.35;
    border-bottom-left-radius: 0.357rem;
    border-bottom-right-radius: 0.357rem;
    strong { color: #7367f0; }
  }
}
</style>
